import { Select } from "antd";
import { useTranslation } from "react-i18next";

import {
  filterOperatorOption,
  operatorName,
} from "@/modules/integrations/accounts";
import type { Operator } from "@/providers/smarta-accounts/accounts-api-client";
import { Lead } from "./types";

interface Props {
  lead: Lead;
  operators: Operator[];
  saving: boolean;
  onChange: (lead: Lead, operatorId: string | undefined) => void;
}

/*
 * The current assignee always comes from the lead itself. An operator who has
 * been disabled, whose role can no longer own leads, or who is missing from the
 * list for any other reason, is added as an option of their own so the select
 * shows their name, not an id. Both use the display name, so the fallback
 * reads the same as an option.
 */
const toOptions = (lead: Lead, operators: Operator[]) => {
  const options = operators.map((operator) => ({
    value: operator.id,
    label: operatorName(operator),
  }));
  const current = lead.manager;
  if (current && !operators.some((operator) => operator.id === current.id)) {
    options.push({ value: current.id, label: current.displayName });
  }
  return options;
};

/**
 * The row's assignee. Controlled by `lead.manager`, so a failed
 * change needs no rollback: the lead is only replaced when the API answers.
 */
export const AssigneeSelect = ({
  lead,
  operators,
  saving,
  onChange,
}: Props) => {
  const { t } = useTranslation("Onboarding");
  const current = lead.manager?.id;

  return (
    <Select<string>
      aria-label={String(t("assignee.label", { name: lead.displayName }))}
      style={{ minWidth: 180, width: "100%" }}
      value={current}
      placeholder={t("leads.unassigned")}
      allowClear
      showSearch
      filterOption={filterOperatorOption(operators)}
      disabled={saving}
      loading={saving}
      options={toOptions(lead, operators)}
      onChange={(operatorId?: string) => {
        if (operatorId !== current) onChange(lead, operatorId);
      }}
    />
  );
};
